import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Button, message, Space, Divider, Alert, Radio, List, Typography, Flex, Tag } from 'antd';
import { settingsAPI } from '../services/api';
import { SoundOutlined, SwapOutlined, FileOutlined, CheckCircleFilled } from '@ant-design/icons';
import {
  RANDOM_RINGTONES,
  DEFAULT_RINGTONE_URL,
  buildRingtoneUrl,
  resolveRingtoneUrl,
} from '../utils/alertSound';

const { Text } = Typography;

const Settings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [thresholdLoading, setThresholdLoading] = useState(false);
  const [emailAlertThreshold, setEmailAlertThreshold] = useState(3);
  const [consecutiveTimeoutThreshold, setConsecutiveTimeoutThreshold] = useState(5);
  const [timeoutDbDefault, setTimeoutDbDefault] = useState(null);
  const [timeoutStoredInDb, setTimeoutStoredInDb] = useState(false);
  const [timeoutThresholdLoading, setTimeoutThresholdLoading] = useState(false);
  const [timeoutForm] = Form.useForm();
  const [ringtoneMode, setRingtoneMode] = useState(() => {
    return localStorage.getItem('pingpulse_ringtone_mode') || 'fixed';
  });
  const [selectedRingtone, setSelectedRingtone] = useState(() => {
    return localStorage.getItem('pingpulse_selected_ringtone') || '';
  });
  const [previewing, setPreviewing] = useState(false);
  const previewAudioRef = React.useRef(null);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConsecutiveTimeoutSettings = async (fromGetAll) => {
    try {
      const timeoutRes = await settingsAPI.getConsecutiveTimeoutThreshold();
      if (!timeoutRes.success || !timeoutRes.data) {
        return;
      }
      const { consecutive_timeout_threshold: effective, database_value, stored_in_database } = timeoutRes.data;
      const dbVal = stored_in_database && database_value != null
        ? database_value
        : (fromGetAll != null ? parseInt(fromGetAll, 10) : null);
      setTimeoutStoredInDb(Boolean(stored_in_database));
      setTimeoutDbDefault(dbVal);
      const formVal = effective ?? dbVal ?? 5;
      setConsecutiveTimeoutThreshold(formVal);
      timeoutForm.setFieldValue('consecutiveTimeoutThreshold', formVal);
    } catch {
      // keep initial state
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getAll();
      if (response.success && response.data) {
        // response.data is an object like { email_alert_threshold: '3', ringtone_mode: 'random', ... }
        const data = response.data;
        if (data.email_alert_threshold) {
          setEmailAlertThreshold(parseInt(data.email_alert_threshold, 10));
          form.setFieldValue('emailAlertThreshold', parseInt(data.email_alert_threshold, 10));
        }
        await loadConsecutiveTimeoutSettings(data.consecutive_timeout_threshold);
        if (data.ringtone_mode) {
          setRingtoneMode(data.ringtone_mode);
          localStorage.setItem('pingpulse_ringtone_mode', data.ringtone_mode);
        }
        if (data.selected_ringtone) {
          setSelectedRingtone(data.selected_ringtone);
          localStorage.setItem('pingpulse_selected_ringtone', data.selected_ringtone);
        }
      } else {
        await loadConsecutiveTimeoutSettings();
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      await loadConsecutiveTimeoutSettings();
    }
  };

  const handleSaveTimeoutThreshold = async (values) => {
    setTimeoutThresholdLoading(true);
    try {
      const response = await settingsAPI.setConsecutiveTimeoutThreshold(values.consecutiveTimeoutThreshold);
      if (response.success) {
        message.success('Consecutive timeout threshold saved');
        const saved = values.consecutiveTimeoutThreshold;
        setConsecutiveTimeoutThreshold(saved);
        setTimeoutDbDefault(saved);
        setTimeoutStoredInDb(true);
      }
    } catch (error) {
      message.error('Failed to save consecutive timeout threshold');
    } finally {
      setTimeoutThresholdLoading(false);
    }
  };

  const handleSaveThreshold = async (values) => {
    setThresholdLoading(true);
    try {
      const response = await settingsAPI.set('email_alert_threshold', values.emailAlertThreshold.toString());
      if (response.success) {
        message.success('Email alert threshold saved successfully');
        setEmailAlertThreshold(values.emailAlertThreshold);
      }
    } catch (error) {
      message.error('Failed to save settings');
    } finally {
      setThresholdLoading(false);
    }
  };

  const handleRingtoneModeChange = async (e) => {
    const mode = e.target.value;
    setRingtoneMode(mode);
    localStorage.setItem('pingpulse_ringtone_mode', mode);

    if (mode === 'fixed') {
      setSelectedRingtone('');
      localStorage.removeItem('pingpulse_selected_ringtone');
    }

    try {
      await settingsAPI.set('ringtone_mode', mode);
      if (mode === 'fixed') {
        await settingsAPI.set('selected_ringtone', '');
      }
      message.success(`Ringtone mode set to: ${mode === 'random' ? 'Random' : 'Fixed'}`);
    } catch (error) {
      message.error('Failed to save ringtone mode');
    }
  };

  const handleSelectRingtone = async (ringtone) => {
    setSelectedRingtone(ringtone);
    localStorage.setItem('pingpulse_selected_ringtone', ringtone);

    try {
      await settingsAPI.set('selected_ringtone', ringtone);
      message.success(`Ringtone selected: ${ringtone}`);
    } catch (error) {
      message.error('Failed to save ringtone selection');
    }
  };

  const playPreviewUrl = async (url, previewKey) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setPreviewing(previewKey);

    const tryPlay = async (src) => {
      const audio = new Audio(src);
      previewAudioRef.current = audio;
      audio.volume = 0.6;
      await audio.play();
    };

    try {
      await tryPlay(url);
    } catch {
      if (url !== DEFAULT_RINGTONE_URL) {
        try {
          await tryPlay(DEFAULT_RINGTONE_URL);
        } catch {
          message.warning('File tidak ditemukan — pastikan ada di public/notifikasi');
        }
      } else {
        message.warning('notifikasi.mp3 tidak ditemukan di public/notifikasi');
      }
    }
    setTimeout(() => setPreviewing(false), 3000);
  };

  const playPreviewRandom = (ringtone) => {
    playPreviewUrl(buildRingtoneUrl(ringtone, 'random'), ringtone);
  };

  const playPreviewFixed = () => {
    playPreviewUrl(resolveRingtoneUrl({ mode: 'fixed', selected: '' }), 'fixed-default');
  };

  const stopPreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewing(false);
  };

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <Card title="Notification Settings" style={{ marginBottom: 16 }}>
        <Alert
          message="Email Notification Buffering"
          description="When a target goes down, notifications are buffered and only sent after consecutive failures reach the threshold. This prevents notification spam during temporary network issues."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveThreshold}
          initialValues={{ emailAlertThreshold }}
        >
          <Form.Item
            name="emailAlertThreshold"
            label="Consecutive Failure Threshold"
            extra="Number of consecutive failures before sending email notification (default: 3)"
          >
            <InputNumber min={1} max={20} style={{ width: 200 }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={thresholdLoading}>
                Save Threshold
              </Button>
              <Button onClick={() => form.resetFields()}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Divider />

        <h4>Current Value: {emailAlertThreshold} consecutive failures</h4>
        <p style={{ color: '#888', fontSize: 12 }}>
          If a target is DOWN or TIMEOUT for {emailAlertThreshold} consecutive checks, an email is sent.
          Dashboard popups use the same counter at threshold. Recovery (UP) does not send email or popup.
        </p>
      </Card>

      <Card title="Target Status: Timeout → DOWN" style={{ marginBottom: 16 }}>
        <Alert
          message="Consecutive timeout threshold"
          description="After this many consecutive TIMEOUT checks, the target status is promoted to DOWN. Applies to all targets unless overridden per target in options."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={timeoutForm}
          layout="vertical"
          onFinish={handleSaveTimeoutThreshold}
          initialValues={{ consecutiveTimeoutThreshold }}
        >
          <Form.Item
            name="consecutiveTimeoutThreshold"
            label="Consecutive TIMEOUT → DOWN threshold"
            extra={
              timeoutStoredInDb && timeoutDbDefault != null
                ? `How many consecutive TIMEOUTs before status becomes DOWN. (default: ${timeoutDbDefault})`
                : `Not yet saved in database. Current server value: ${consecutiveTimeoutThreshold}. Save to write it into the database.`
            }
          >
            <InputNumber min={1} max={50} style={{ width: 200 }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={timeoutThresholdLoading}>
                Save Threshold
              </Button>
              <Button
                onClick={() => {
                  const resetVal = timeoutDbDefault ?? consecutiveTimeoutThreshold;
                  timeoutForm.setFieldsValue({ consecutiveTimeoutThreshold: resetVal });
                }}
              >
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Divider />

        <h4>
          Current value: {consecutiveTimeoutThreshold} consecutive timeouts
          {timeoutStoredInDb && timeoutDbDefault != null && (
            <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>
              {' '}(default database: {timeoutDbDefault})
            </span>
          )}
        </h4>
        <p style={{ color: '#888', fontSize: 12 }}>
          Example: with threshold {consecutiveTimeoutThreshold}, checks 1–{consecutiveTimeoutThreshold - 1} stay TIMEOUT;
          check {consecutiveTimeoutThreshold} logs and displays as DOWN. Counter resets when the target is UP.
        </p>
      </Card>

      <Card title="Alert Sound Settings" style={{ marginBottom: 16 }}>
        <Alert
          message="Ringtone Selection"
          description="Choose how alerts are played: 'Random' plays a random ringtone from the collection, 'Fixed' plays the default notifikasi.mp3 file."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form layout="vertical">
          <Form.Item label="Ringtone Mode">
            <Radio.Group onChange={handleRingtoneModeChange} value={ringtoneMode}>
              <Space direction="vertical">
                <Radio value="random">
                  <Space>
                    <SwapOutlined />
                    <span>Random - Play random ringtone for each alert</span>
                  </Space>
                </Radio>
                <Radio value="fixed">
                  <Space>
                    <FileOutlined />
                    <span>Fixed - Always play notifikasi.mp3</span>
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Form>

        {ringtoneMode === 'random' && (
          <>
            <Divider />
            <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
              <Text strong>Daftar ringtone ({RANDOM_RINGTONES.length})</Text>
              {selectedRingtone ? (
                <Tag color="blue" icon={<CheckCircleFilled />}>
                  {selectedRingtone}
                </Tag>
              ) : (
                <Tag>Belum dipilih — akan random dari semua file</Tag>
              )}
            </Flex>
            <List
              bordered
              size="small"
              style={{ maxHeight: 320, overflowY: 'auto' }}
              dataSource={RANDOM_RINGTONES}
              renderItem={(ringtone) => {
                const isSelected = selectedRingtone === ringtone;
                const isPreviewing = previewing === ringtone;
                return (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      padding: '10px 12px',
                      background: isSelected ? '#e6f7ff' : undefined,
                    }}
                    onClick={() => handleSelectRingtone(ringtone)}
                    actions={[
                      isPreviewing ? (
                        <Button
                          key="stop"
                          size="small"
                          type="link"
                          onClick={(e) => { e.stopPropagation(); stopPreview(); }}
                        >
                          Stop
                        </Button>
                      ) : (
                        <Button
                          key="preview"
                          size="small"
                          type="link"
                          icon={<SoundOutlined />}
                          onClick={(e) => { e.stopPropagation(); playPreviewRandom(ringtone); }}
                        >
                          Preview
                        </Button>
                      ),
                    ]}
                  >
                    <Flex align="center" gap={12} style={{ minWidth: 0, flex: 1 }}>
                      {isSelected ? (
                        <CheckCircleFilled style={{ color: '#1890ff', fontSize: 18, flexShrink: 0 }} />
                      ) : (
                        <SoundOutlined style={{ color: '#bfbfbf', fontSize: 18, flexShrink: 0 }} />
                      )}
                      <Text
                        ellipsis
                        style={{ flex: 1, fontWeight: isSelected ? 600 : 400 }}
                        title={ringtone}
                      >
                        {ringtone}
                      </Text>
                    </Flex>
                  </List.Item>
                );
              }}
            />
          </>
        )}

        {ringtoneMode === 'fixed' && (
          <>
            <Divider />
            <Space align="start">
              <FileOutlined style={{ fontSize: 24 }} />
              <div>
                <Text strong>notifikasi.mp3</Text>
                <br />
                <Text type="secondary">Default alert sound</Text>
              </div>
              {previewing === 'fixed-default' ? (
                <Button size="small" onClick={stopPreview}>Stop</Button>
              ) : (
                <Button size="small" icon={<SoundOutlined />} onClick={playPreviewFixed}>
                  Preview
                </Button>
              )}
            </Space>
          </>
        )}
      </Card>
    </div>
  );
};

export default Settings;